/**
 * ConversationView Component
 * Main chat interface. Reads all data from Supabase.
 * Includes Bot/Human mode toggle and operator assignment.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import {
  Send, Loader2, Sparkles, AlertTriangle, ChevronDown, ChevronUp,
  Check, MessageCircle, StickyNote, X, Bot, User, FileText, FlaskConical, Pencil, Clock,
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { ModerationContact, ModerationMessage, FlagType, DetectedItem } from "../types"
import { useMessages, useFlags, useRatings, useComments } from "../hooks"
import { supabase as supabaseClient } from "@/lib/data"
import { MessageBubble, type EvalItemMap } from "./MessageBubble"
import { QuickRepliesPanel } from "./QuickRepliesPanel"
import { formatTimestamp } from "../utils"
import { isIncomingMessage, messageHasActiveModerationFlags, expandBreaks } from "../utils/conversationUtils"
import { supabase } from "@/lib/data"
import type { MessageComment } from "../hooks/useComments"

// Stored in contacts.moderation_data JSON
interface ContactData {
  handled_by?: "bot" | "human"
  assigned_to_id?: string
  assigned_to_name?: string
  last_read_message_id?: string
  last_read_message_created_at?: string
  last_read_at?: string
  /** Moderator-set: parks a conversation in the "Pending" category — no action needed now, but not resolved either. */
  pending?: boolean
}

interface MessageTemplate {
  id: string
  title: string
  body: string
  sort_order?: number | null
}

function parseContactData(raw: string | null | undefined): ContactData {
  if (!raw) return {}
  try { return JSON.parse(raw) } catch { return {} }
}

const SUMMARY_CLAMP_CHARS = 160
const DECORSIFY_PROXY = "https://signpost-ia-app-qa.azurewebsites.net/decorsify/"
const telerivetRouteNameCache = new Map<string, string>()

async function parseJsonSafe(response: Response): Promise<unknown> {
  const raw = await response.text()
  if (!raw || !raw.trim()) return null
  return JSON.parse(raw) as unknown
}

async function fetchTelerivetRouteName(projectId: string, routeId: string, apiKey: string): Promise<string | null> {
  if (!projectId || !routeId || !apiKey) return null
  const cacheKey = `${projectId}:${routeId}`
  if (telerivetRouteNameCache.has(cacheKey)) return telerivetRouteNameCache.get(cacheKey) || null

  const endpoint = `https://api.telerivet.com/v1/projects/${projectId}/route_list?api_key=${encodeURIComponent(apiKey)}`
  const loadRouteList = async (url: string) => {
    const resp = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } })
    if (!resp.ok) throw new Error(`Failed to fetch route list (${resp.status})`)
    const parsed = await parseJsonSafe(resp)
    return Array.isArray(parsed) ? parsed : []
  }

  let rows: unknown[] = []
  try {
    rows = await loadRouteList(endpoint)
  } catch {
    rows = await loadRouteList(`${DECORSIFY_PROXY}${endpoint}`)
  }

  for (const row of rows) {
    if (!row || typeof row !== "object") continue
    const asObj = row as Record<string, unknown>
    if (asObj.id === routeId) {
      const name = typeof asObj.name === "string" && asObj.name.trim() ? asObj.name : routeId
      telerivetRouteNameCache.set(cacheKey, name)
      return name
    }
  }

  telerivetRouteNameCache.set(cacheKey, routeId)
  return routeId
}

function SummaryText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = text.length > SUMMARY_CLAMP_CHARS
  const display = !isLong || expanded ? text : text.slice(0, SUMMARY_CLAMP_CHARS).trimEnd() + "…"
  return (
    <p className="text-[10px] text-muted-foreground leading-relaxed">
      {display}
      {isLong && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="ml-1 text-[10px] font-medium text-primary hover:underline focus:outline-hidden shrink-0"
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </p>
  )
}

interface ConversationViewProps {
  contact: ModerationContact
  availableUsers?: Array<{ id: string; email: string; name?: string }>
  currentUserId?: string
  currentUserOperatorContactId?: string
  onUpdateFlags?: (contactId: string, messages: ModerationMessage[]) => void
  onContactUpdated?: (contactId: string, updates: Partial<ModerationContact>) => void
  /** Reports the agent that handled this conversation (last assistant message's agent). */
  onAgentIdChange?: (contactId: string, agentId: number | null) => void
}

export function ConversationView({
  contact,
  availableUsers = [],
  currentUserId,
  currentUserOperatorContactId,
  onUpdateFlags,
  onContactUpdated,
  onAgentIdChange,
}: ConversationViewProps) {
  const [messageContent, setMessageContent]   = useState("")
  const [sending, setSending]                 = useState(false)
  const [showQuickReplies, setShowQuickReplies] = useState(false)
  const [currentFlagIndex, setCurrentFlagIndex] = useState(0)
  const [comments, setComments]               = useState<MessageComment[]>([])
  const [noteOpen, setNoteOpen]               = useState(false)
  const [noteText, setNoteText]               = useState("")
  const [savingNote, setSavingNote]           = useState(false)
  const [evalItemMap, setEvalItemMap]         = useState<EvalItemMap>({})
  const [agentFlags, setAgentFlags]           = useState<AgentEscalationFlag[]>([])
  const [agentTitles, setAgentTitles]         = useState<Record<number, string>>({})
  const [ratingOverrides, setRatingOverrides] = useState<Record<string, "0" | "1">>({})
  /** Until refresh completes, keep merged moderation data so UI matches optimistic parent while live row is stale */
  const [pendingModerationData, setPendingModerationData] = useState<string | null>(null)
  const [pendingHitl, setPendingHitl] = useState<boolean | null>(null)
  const [templates, setTemplates] = useState<MessageTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState("")
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [newTemplateTitle, setNewTemplateTitle] = useState("")
  const [newTemplateBody, setNewTemplateBody] = useState("")
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null)
  const [routeDisplayName, setRouteDisplayName] = useState<string | null>(null)
  const markingReadRef = useRef(false)
  const lastMarkedMessageIdRef = useRef<string | null>(null)

  const scrollRef    = useRef<HTMLDivElement>(null)
  const messageRefs  = useRef<Map<string, HTMLDivElement>>(new Map())
  const { toast }    = useToast()
  const navigate     = useNavigate()

  const { messages: rawMessages, loading, sendMessage, refresh: refreshMessages, contact: liveContact, optimisticUpdateMessage } = useMessages(contact.id)
  const { toggleFlag, resolveAllFlags } = useFlags()
  const { submitRating } = useRatings()
  const { getComments, addComment, deleteComment, getCommentsForMessage, getConversationComments } = useComments(contact.id)

  // Apply optimistic rating overrides so the UI updates immediately
  const messages = rawMessages.map(m =>
    ratingOverrides[m.id] !== undefined ? { ...m, rating: ratingOverrides[m.id] } : m
  )

  const latestTelerivetRouteMeta = useMemo(() => {
    for (let i = rawMessages.length - 1; i >= 0; i--) {
      const msg = rawMessages[i]
      if (msg.channel !== "telerivet") continue
      const integration = (msg.integration || {}) as Record<string, unknown>
      const routeId = typeof integration.route_id === "string" ? integration.route_id : null
      const projectId = (
        (typeof integration.projectId === "string" && integration.projectId) ||
        (typeof integration.project_id === "string" && integration.project_id) ||
        null
      )
      if (routeId && projectId) return { routeId, projectId }
    }
    return null
  }, [rawMessages])
  const routeDisplayLabel = useMemo(() => {
    if (!routeDisplayName) return null
    const cleaned = routeDisplayName.replace(/\s*<[^>]+>\s*$/, "").trim()
    return cleaned || routeDisplayName
  }, [routeDisplayName])

  useEffect(() => { getComments().then(setComments) }, [contact.id, getComments])

  useEffect(() => {
    let cancelled = false
    const loadRouteName = async () => {
      if (!latestTelerivetRouteMeta || !contact.team) {
        setRouteDisplayName(null)
        return
      }
      try {
        const { data: keyRows } = await supabase
          .from("api_keys")
          .select("type, key")
          .eq("team_id", contact.team)
        const apiKey = (keyRows || []).find((row: any) => row.type === "telerivet")?.key
        if (!apiKey) {
          if (!cancelled) setRouteDisplayName(latestTelerivetRouteMeta.routeId)
          return
        }
        const resolvedName = await fetchTelerivetRouteName(
          latestTelerivetRouteMeta.projectId,
          latestTelerivetRouteMeta.routeId,
          apiKey
        )
        if (!cancelled) setRouteDisplayName(resolvedName)
      } catch {
        if (!cancelled) setRouteDisplayName(latestTelerivetRouteMeta.routeId)
      }
    }
    void loadRouteName()
    return () => { cancelled = true }
  }, [latestTelerivetRouteMeta, contact.team])

  // Fetch eval items once for tag labels
  useEffect(() => {
    supabase.from("eval_items").select("id, name, highrisk, lowconf, askhuman").then(({ data }) => {
      if (data) {
        const map: EvalItemMap = {}
        for (const item of data) {
          map[item.id as number] = {
            name: item.name ?? "",
            highrisk: item.highrisk,
            lowconf: item.lowconf,
            askhuman: item.askhuman,
          }
        }
        setEvalItemMap(map)
      }
    })
  }, [])

  useEffect(() => {
    if (!contact.team) {
      setTemplates([])
      setSelectedTemplateId("")
      return
    }
    let cancelled = false
    const loadTemplates = async () => {
      setTemplatesLoading(true)
      try {
        const { data, error } = await supabase
          .from("team_message_templates" as any)
          .select("id, title, body, sort_order")
          .eq("team_id", contact.team)
          .order("sort_order", { ascending: true })
          .order("title", { ascending: true })

        if (cancelled) return
        if (error) {
          console.error("[ConversationView] failed to load message templates:", error)
          setTemplates([])
          return
        }

        const rows = ((data ?? []) as unknown) as Array<Record<string, unknown>>
        const normalizedTemplates: MessageTemplate[] = []
        for (const row of rows) {
          const id = typeof row.id === "string" ? row.id : null
          const title = typeof row.title === "string" ? row.title : null
          const body = typeof row.body === "string" ? row.body : null
          if (!id || !title || !body) continue
          normalizedTemplates.push({
            id,
            title,
            body,
            sort_order: typeof row.sort_order === "number" ? row.sort_order : null,
          })
        }
        setTemplates(normalizedTemplates)
      } finally {
        if (!cancelled) setTemplatesLoading(false)
      }
    }
    void loadTemplates()
    return () => { cancelled = true }
  }, [contact.team, contact.id])

  // Derive agent ID from the most recent assistant message
  const agentId = useMemo(
    () => [...rawMessages].reverse().find(m => m.role === "assistant" && m.agent)?.agent ?? null,
    [rawMessages]
  )

  // Report the derived agent up so siblings (e.g. the Forms panel) can scope to it.
  // Only report once messages have finished loading — while loading, `agentId`
  // is derived from an empty (or previous) thread and would misreport the agent
  // for this contact, briefly scoping the Forms panel to the wrong agent.
  const onAgentIdChangeRef = useRef(onAgentIdChange)
  onAgentIdChangeRef.current = onAgentIdChange
  useEffect(() => {
    if (loading) return
    onAgentIdChangeRef.current?.(contact.id, agentId)
  }, [contact.id, agentId, loading])

  // Simulated/synthetic conversations can be continued in the Simulator, where
  // the operator acts as the user against the same agent + historical context.
  const isSimulated = contact.type === "synthetic"
  const openInSimulator = useCallback(() => {
    const params = new URLSearchParams()
    params.set("contact", contact.id)
    if (agentId != null) params.set("agent", String(agentId))
    navigate(`/simulator?${params.toString()}`)
  }, [contact.id, agentId, navigate])

  // Fetch agent's escalation flags whenever the agent ID changes
  useEffect(() => {
    if (!agentId) { setAgentFlags([]); return }
    let cancelled = false
    supabaseClient.from("agents").select("config").eq("id", agentId).single().then(({ data }) => {
      if (cancelled) return
      const config = data?.config as any
      setAgentFlags(config?.escalation_flags || [])
    })
    return () => { cancelled = true }
  }, [agentId])

  // Load id→title for the team's agents so each bot message can show which
  // agent generated it (a conversation may span more than one agent).
  useEffect(() => {
    if (!contact.team) { setAgentTitles({}); return }
    let cancelled = false
    supabaseClient.from("agents").select("id, title").eq("team_id", contact.team).then(({ data }) => {
      if (cancelled) return
      const map: Record<number, string> = {}
      for (const a of data || []) if (a.id != null) map[a.id as number] = (a.title as string) || `Agent ${a.id}`
      setAgentTitles(map)
    })
    return () => { cancelled = true }
  }, [contact.team])

  // Keep a stable ref to onUpdateFlags so the effect below doesn't
  // re-register every time the parent re-renders with a new function identity.
  const onUpdateFlagsRef = useRef(onUpdateFlags)
  onUpdateFlagsRef.current = onUpdateFlags

  // Call onUpdateFlags only when messages change *after* the initial load for
  // this contact. The contact list already has flag data from the 10s poll;
  // firing on first load would cause an immediate unnecessary re-sort.
  const hasLoadedRef = useRef<string | null>(null)
  const lastMessagesFingerprintRef = useRef<string>("")
  useEffect(() => {
    if (messages.length === 0) return
    const fingerprint = messages.map(m => m.id).join(",")
    if (hasLoadedRef.current !== contact.id) {
      // First load for this contact — record state but don't call updateFlags
      hasLoadedRef.current = contact.id
      lastMessagesFingerprintRef.current = fingerprint
      return
    }
    if (fingerprint === lastMessagesFingerprintRef.current) return
    lastMessagesFingerprintRef.current = fingerprint
    onUpdateFlagsRef.current?.(contact.id, messages)
  }, [messages, contact.id])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages])

  useEffect(() => { setCurrentFlagIndex(0) }, [contact.id])
  useEffect(() => { setSelectedTemplateId("") }, [contact.id])

  // Merge: optimistic moderation data during local save; otherwise prefer live row (realtime)
  // so another operator's bot/human change is not masked by a stale selected contact.
  const mergedModerationData =
    pendingModerationData ?? (liveContact?.moderation_data as string | undefined) ?? (contact.moderation_data as string | undefined)
  const mergedHitl = pendingHitl ?? liveContact?.hitl ?? contact.hitl ?? false
  const effectiveContact: ModerationContact = liveContact
    ? { ...liveContact, moderation_data: mergedModerationData, hitl: mergedHitl }
    : { ...contact, moderation_data: mergedModerationData, hitl: mergedHitl }
  const contactData = parseContactData(effectiveContact.moderation_data as string)
  const handledBy        = contactData.handled_by ?? (effectiveContact.hitl ? "human" : "bot")
  const assignedToId     = contactData.assigned_to_id ?? ""
  const assignedToName   = contactData.assigned_to_name ?? ""
  const noReplyNeeded    = effectiveContact.no_reply_needed === true

  useEffect(() => {
    if (markingReadRef.current) return
    const latestIncoming = [...messages].reverse().find(m => isIncomingMessage(m))
    if (!latestIncoming) return

    const parsed = parseContactData((mergedModerationData as string) || "")
    if (parsed.last_read_message_id === latestIncoming.id) {
      lastMarkedMessageIdRef.current = latestIncoming.id
      return
    }
    if (lastMarkedMessageIdRef.current === latestIncoming.id) return

    const markConversationRead = async () => {
      markingReadRef.current = true
      const merged = {
        ...parsed,
        last_read_message_id: latestIncoming.id,
        last_read_message_created_at: latestIncoming.created_at,
        last_read_at: new Date().toISOString(),
      }
      const moderationData = JSON.stringify(merged)
      const { error } = await supabase
        .from("contacts")
        .update({ moderation_data: moderationData } as any)
        .eq("id", contact.id)

      if (!error) {
        lastMarkedMessageIdRef.current = latestIncoming.id
        onContactUpdated?.(contact.id, { moderation_data: moderationData as any })
      }
      markingReadRef.current = false
    }

    void markConversationRead()
  }, [contact.id, mergedModerationData, messages, onContactUpdated])

  // Persist moderation contact data changes to Supabase.
  // Always read from `contact.moderation_data` (prop) as the base — it's kept up to date
  // by onContactUpdated immediately, avoiding stale-poll overwrites.
  const updateContactData = useCallback(async (updates: Partial<ContactData>) => {
    const base = parseContactData(
      (pendingModerationData ?? (contact.moderation_data as string | undefined)) as string | undefined
    )
    const merged  = { ...base, ...updates }
    const newModerationData = JSON.stringify(merged)
    const newHitl = merged.handled_by === "human"
    const { error } = await supabase
      .from("contacts")
      .update({ moderation_data: newModerationData, hitl: newHitl } as any)
      .eq("id", contact.id)
    if (!error) {
      setPendingModerationData(newModerationData)
      setPendingHitl(newHitl)
      onContactUpdated?.(contact.id, { moderation_data: newModerationData as any, hitl: newHitl })
      try {
        await refreshMessages({ silent: true })
      } finally {
        setPendingModerationData(null)
        setPendingHitl(null)
      }
    }
    return error
  }, [contact.id, contact.moderation_data, onContactUpdated, pendingModerationData, refreshMessages])

  const handleToggleSwitchboard = async () => {
    const newMode: "bot" | "human" = handledBy === "bot" ? "human" : "bot"
    if (newMode === "human" && currentUserId) {
      const me = availableUsers.find(u => u.id === currentUserId)
      await updateContactData({
        handled_by: "human",
        assigned_to_id: currentUserId,
        assigned_to_name: me?.name || me?.email || currentUserId,
      })
    } else {
      await updateContactData({ handled_by: newMode })
    }
  }

  const handleAssignment = async (userId: string) => {
    const user = availableUsers.find(u => u.id === userId)
    if (!user) return
    await updateContactData({
      assigned_to_id:   userId,
      assigned_to_name: user.name || user.email,
      handled_by:       "human",
    })
  }

  const flaggedMessages = messages.filter(m => messageHasActiveModerationFlags(m, evalItemMap))

  const conversationComments = useMemo(() => getConversationComments(comments), [comments, getConversationComments])
  const timeline = useMemo(() => {
    const items: Array<{ type: "message"; message: ModerationMessage } | { type: "comment"; comment: MessageComment }> = []
    // Split bot responses on <break> so each part shows as its own bubble,
    // matching how they were actually delivered to the user.
    expandBreaks(messages).forEach(m => items.push({ type: "message", message: m }))
    conversationComments.forEach(c => items.push({ type: "comment", comment: c }))
    items.sort((a, b) => {
      const ta = a.type === "message" ? new Date(a.message.created_at).getTime() : a.comment.created_at * 1000
      const tb = b.type === "message" ? new Date(b.message.created_at).getTime() : b.comment.created_at * 1000
      return ta - tb
    })
    return items
  }, [messages, conversationComments])

  const jumpToFlagged = (direction: "next" | "prev") => {
    if (flaggedMessages.length === 0) return
    const newIndex = direction === "next"
      ? (currentFlagIndex + 1) % flaggedMessages.length
      : currentFlagIndex - 1 < 0 ? flaggedMessages.length - 1 : currentFlagIndex - 1
    setCurrentFlagIndex(newIndex)
    const el = messageRefs.current.get(flaggedMessages[newIndex].id)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  const handleSend = async () => {
    if (!messageContent.trim() || sending) return
    setSending(true)
    try {
      await sendMessage(
        messageContent,
        handledBy === "human" ? currentUserOperatorContactId : undefined
      )
      setMessageContent("")
      toast({ title: "Message sent" })
    } catch (err) {
      const description = err instanceof Error ? err.message : "Failed to send message."
      toast({ title: "Error", description, variant: "destructive" })
    } finally {
      setSending(false)
    }
  }

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId)
    const selectedTemplate = templates.find(template => template.id === templateId)
    if (!selectedTemplate) return
    setMessageContent(selectedTemplate.body)
  }

  const handleUseTemplate = (template: MessageTemplate) => {
    setSelectedTemplateId(template.id)
    setMessageContent(template.body)
    setTemplateModalOpen(false)
  }

  const resetTemplateForm = () => {
    setNewTemplateTitle("")
    setNewTemplateBody("")
    setEditingTemplateId(null)
  }

  const handleTemplateModalChange = (open: boolean) => {
    setTemplateModalOpen(open)
    if (!open) resetTemplateForm()
  }

  const handleEditTemplate = (template: MessageTemplate) => {
    setEditingTemplateId(template.id)
    setNewTemplateTitle(template.title)
    setNewTemplateBody(template.body)
  }

  const handleSaveTemplate = async () => {
    if (!contact.team) {
      toast({ title: "No team selected", description: "Cannot save template without a team.", variant: "destructive" })
      return
    }
    const title = newTemplateTitle.trim()
    const body = newTemplateBody.trim()
    if (!title || !body || savingTemplate) return

    setSavingTemplate(true)
    try {
      if (editingTemplateId) {
        const { data, error } = await supabase
          .from("team_message_templates" as any)
          .update({
            title,
            body,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingTemplateId)
          .select("id, title, body, sort_order")
          .single()

        if (error) {
          toast({ title: "Error", description: "Failed to update template.", variant: "destructive" })
          return
        }

        const row = (data ?? {}) as Record<string, unknown>
        const updatedTemplate: MessageTemplate | null =
          typeof row.id === "string" && typeof row.title === "string" && typeof row.body === "string"
            ? {
                id: row.id,
                title: row.title,
                body: row.body,
                sort_order: typeof row.sort_order === "number" ? row.sort_order : null,
              }
            : null

        if (updatedTemplate) {
          setTemplates(prev => prev.map(template =>
            template.id === updatedTemplate.id ? updatedTemplate : template
          ).sort((a, b) => {
            const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0)
            return byOrder !== 0 ? byOrder : a.title.localeCompare(b.title)
          }))
          if (selectedTemplateId === updatedTemplate.id) {
            setMessageContent(updatedTemplate.body)
          }
        }
        resetTemplateForm()
        toast({ title: "Template updated" })
        return
      }

      const nextSortOrder = templates.length > 0
        ? Math.max(...templates.map(t => t.sort_order ?? 0)) + 1
        : 0
      const { data, error } = await supabase
        .from("team_message_templates" as any)
        .insert({
          team_id: contact.team,
          title,
          body,
          sort_order: nextSortOrder,
          created_by: currentUserId ?? null,
        })
        .select("id, title, body, sort_order")
        .single()

      if (error) {
        toast({ title: "Error", description: "Failed to create template.", variant: "destructive" })
        return
      }

      const row = (data ?? {}) as Record<string, unknown>
      const createdTemplate: MessageTemplate | null =
        typeof row.id === "string" && typeof row.title === "string" && typeof row.body === "string"
          ? {
              id: row.id,
              title: row.title,
              body: row.body,
              sort_order: typeof row.sort_order === "number" ? row.sort_order : null,
            }
          : null

      if (createdTemplate) {
        setTemplates(prev => [...prev, createdTemplate].sort((a, b) => {
          const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0)
          return byOrder !== 0 ? byOrder : a.title.localeCompare(b.title)
        }))
        handleUseTemplate(createdTemplate)
      }
      resetTemplateForm()
      toast({ title: "Template created" })
    } finally {
      setSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    if (deletingTemplateId) return
    setDeletingTemplateId(templateId)
    try {
      const { error } = await supabase
        .from("team_message_templates" as any)
        .delete()
        .eq("id", templateId)

      if (error) {
        toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" })
        return
      }

      setTemplates(prev => prev.filter(template => template.id !== templateId))
      if (selectedTemplateId === templateId) setSelectedTemplateId("")
      if (editingTemplateId === templateId) resetTemplateForm()
      toast({ title: "Template deleted" })
    } finally {
      setDeletingTemplateId(null)
    }
  }

  const handleToggleFlag = async (messageId: string, flagType: FlagType, currentItems: DetectedItem[], currentFlags: FlagDetectedItem[] | undefined, message: ModerationMessage) => {
    try {
      await toggleFlag(messageId, flagType, currentItems, currentFlags, message)
      refreshMessages()
      toast({ title: "Flag updated" })
    } catch {
      toast({ title: "Error", description: "Failed to update flag.", variant: "destructive" })
    }
  }

  const handleResolveAll = async (messageId: string, currentItems: DetectedItem[], currentFlags: FlagDetectedItem[] | undefined, message: ModerationMessage) => {
    try {
      await resolveAllFlags(messageId, currentItems, currentFlags, message)
      refreshMessages()
      toast({ title: "Flags resolved" })
    } catch {
      toast({ title: "Error", description: "Failed to resolve flags.", variant: "destructive" })
    }
  }

  const handleNoReplyNeeded = async () => {
    try {
      await supabase.from("contacts").update({ no_reply_needed: true }).eq("id", contact.id)
      onContactUpdated?.(contact.id, { no_reply_needed: true })
      toast({ title: "Marked as handled" })
    } catch {
      console.error("Failed to mark no reply needed")
    }
  }

  // Manual "Pending" flag — parks a conversation that isn't fully resolved but
  // doesn't need a moderator's attention right now, and shouldn't go back to
  // the AI. Stored in moderation_data (not a DB column) so it merges through
  // the same updateContactData path as handled_by/assigned_to. Note this can
  // be set even on a conversation that currently reads as Open — the Pending
  // flag is intentionally overridden by Open in categorizeConversation, so a
  // moderator marking something pending never hides a fresh escalation.
  const isPending = Boolean(contactData.pending)
  const handleTogglePending = async () => {
    const error = await updateContactData({ pending: !isPending })
    if (!error) {
      toast({ title: isPending ? "Removed from Pending" : "Marked as Pending" })
    } else {
      toast({ title: "Error", description: "Failed to update pending status.", variant: "destructive" })
    }
  }

  const handleAddComment = async (messageId: string, text: string) => {
    const userId   = currentUserId || "unknown"
    const userName = availableUsers.find(u => u.id === userId)?.name
      || availableUsers.find(u => u.id === userId)?.email
      || "Moderator"
    const updated = await addComment(text, userId, userName, messageId)
    setComments(updated)
  }

  const handleAddNote = async () => {
    if (!noteText.trim() || savingNote) return
    setSavingNote(true)
    try {
      const userId   = currentUserId || "unknown"
      const userName = availableUsers.find(u => u.id === userId)?.name
        || availableUsers.find(u => u.id === userId)?.email
        || "Moderator"
      const updated = await addComment(noteText.trim(), userId, userName)
      setComments(updated)
      setNoteText("")
      setNoteOpen(false)
      toast({ title: "Note added" })
    } catch {
      toast({ title: "Error", description: "Failed to add note.", variant: "destructive" })
    } finally {
      setSavingNote(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    const updated = await deleteComment(commentId)
    setComments(updated)
  }


  const lastMessage = messages[messages.length - 1]
  const showNoReplyBtn = handledBy === "human"
    && lastMessage
    && isIncomingMessage(lastMessage)
    && !noReplyNeeded

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">

      {/* ── Header ── */}
      <div className="px-3 pt-2 pb-1.5 border-b space-y-1.5 min-w-0">

        {/* Row 1: name + severity badge + flagged navigator */}
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-semibold truncate flex-1 min-w-0">
            {effectiveContact.name || effectiveContact.id}
          </h2>
          {routeDisplayLabel && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 max-w-[180px] truncate shrink-0" title={routeDisplayName || routeDisplayLabel}>
              {routeDisplayLabel}
            </span>
          )}
          {effectiveContact.severity != null && (() => {
            const sev = effectiveContact.severity!
            const cls =
              sev >= 5 ? "bg-red-600 text-white" :
              sev >= 4 ? "bg-orange-500 text-white" :
              sev >= 3 ? "bg-amber-400 text-amber-900" :
              sev >= 2 ? "bg-yellow-200 text-yellow-800" :
                         "bg-gray-200 text-gray-700"
            return (
              <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0", cls)}>
                Severity {sev}
              </span>
            )
          })()}
          {flaggedMessages.length > 0 && (
            <div className="flex items-center gap-1 text-[11px] text-red-600 shrink-0">
              <AlertTriangle className="h-3 w-3" />
              <span className="font-medium">{flaggedMessages.length} flagged</span>
              <div className="flex items-center border border-red-300 rounded overflow-hidden h-6">
                <button onClick={() => jumpToFlagged("prev")} className="px-1.5 h-full hover:bg-red-100 flex items-center">
                  <ChevronUp className="h-3 w-3" />
                </button>
                <span className="px-1.5 h-full flex items-center bg-red-50 text-[10px] font-medium border-x border-red-300">
                  {currentFlagIndex + 1}/{flaggedMessages.length}
                </span>
                <button onClick={() => jumpToFlagged("next")} className="px-1.5 h-full hover:bg-red-100 flex items-center">
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Row 2: summary — collapsible */}
        {effectiveContact.summary && (
          <SummaryText text={effectiveContact.summary} />
        )}

        {/* Row 3: Bot/Human toggle + assignment + note */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Bot ↔ Human switch */}
          <div className="flex items-center gap-1.5 border rounded px-2 h-7 shrink-0">
            <Bot className={cn("h-3 w-3 transition-colors", handledBy === "bot" ? "text-primary" : "text-muted-foreground/30")} />
            <Switch
              id="switchboard"
              checked={handledBy === "human"}
              onCheckedChange={handleToggleSwitchboard}
              className="scale-75"
            />
            <User className={cn("h-3 w-3 transition-colors", handledBy === "human" ? "text-primary" : "text-muted-foreground/30")} />
          </div>

          {/* Assign to operator */}
          <Select
            value={assignedToId || ""}
            onValueChange={handleAssignment}
            disabled={handledBy === "bot"}
          >
            <SelectTrigger className={cn(
              "h-7 flex-1 min-w-[120px] max-w-[180px] text-[10px]",
              handledBy === "bot" && "opacity-50 pointer-events-none"
            )}>
              <SelectValue placeholder="Assign to…" />
            </SelectTrigger>
            <SelectContent>
              {availableUsers.length === 0 ? (
                <SelectItem value="_none" disabled className="text-xs text-muted-foreground">
                  No users available
                </SelectItem>
              ) : (
                availableUsers.map(user => (
                  <SelectItem key={user.id} value={user.id} className="text-xs">
                    {user.name || user.email}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {/* Add note */}
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 shrink-0" onClick={() => setNoteOpen(true)}>
            <StickyNote className="h-3 w-3" />
            Add note
          </Button>

          {/* Continue this simulated conversation in the Simulator (act as user) */}
          {isSimulated && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[10px] gap-1 shrink-0"
              onClick={openInSimulator}
              title="Continue this conversation in the Simulator as the user"
            >
              <FlaskConical className="h-3 w-3" />
              Simulator
            </Button>
          )}
        </div>
      </div>

      {/* ── Add note dialog ── */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add internal note</DialogTitle>
            <DialogDescription>This note is only visible to your team.</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Type your note..."
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            className="min-h-[80px] text-sm"
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote() } }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!noteText.trim() || savingNote}>
              {savingNote ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</> : "Add note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Templates dialog ── */}
      <Dialog open={templateModalOpen} onOpenChange={handleTemplateModalChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Message templates</DialogTitle>
            <DialogDescription>Select a template to prefill the composer, or create and edit templates.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Existing templates</p>
              <div className="max-h-52 overflow-y-auto rounded-md border p-2 space-y-1.5">
                {templatesLoading ? (
                  <p className="text-xs text-muted-foreground px-1 py-2">Loading templates…</p>
                ) : templates.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1 py-2">No templates yet.</p>
                ) : (
                  templates.map(template => (
                    <div
                      key={template.id}
                      className={cn(
                        "w-full rounded border px-2 py-1.5 transition-colors",
                        editingTemplateId === template.id
                          ? "border-primary bg-primary/5"
                          : selectedTemplateId === template.id
                            ? "border-primary/60 bg-primary/5"
                            : "hover:bg-accent"
                      )}
                    >
                      <div className="flex items-start gap-1">
                        <button
                          type="button"
                          className="flex-1 text-left min-w-0"
                          onClick={() => handleUseTemplate(template)}
                        >
                          <p className="text-xs font-medium">{template.title}</p>
                          <p className="text-[11px] text-muted-foreground line-clamp-2">{template.body}</p>
                        </button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={() => handleEditTemplate(template)}
                          title="Edit template"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => void handleDeleteTemplate(template.id)}
                          disabled={deletingTemplateId === template.id}
                          title="Delete template"
                        >
                          {deletingTemplateId === template.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <X className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {editingTemplateId ? "Edit template" : "Create new template"}
                </p>
                {editingTemplateId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    onClick={resetTemplateForm}
                  >
                    Cancel edit
                  </Button>
                )}
              </div>
              <Input
                placeholder="Template title"
                value={newTemplateTitle}
                onChange={e => setNewTemplateTitle(e.target.value)}
                className="h-8 text-xs"
              />
              <Textarea
                placeholder="Template message body"
                value={newTemplateBody}
                onChange={e => setNewTemplateBody(e.target.value)}
                className="min-h-[88px] text-xs"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleTemplateModalChange(false)}>Close</Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate || !newTemplateTitle.trim() || !newTemplateBody.trim()}>
              {savingTemplate
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</>
                : editingTemplateId ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Messages ── */}
      <ScrollArea className="flex-1 min-h-0 p-4 bg-slate-50/70 dark:bg-slate-900/20 border-y" ref={scrollRef}>
        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-3/4" />)}</div>
        ) : (
          <div className="pt-1">
            {timeline.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <p>No messages yet</p>
              </div>
            ) : (
              timeline.map(item => {
                if (item.type === "comment") {
                  const c = item.comment
                  return (
                    <div key={`comment-${c.id}`} className="mb-2 mx-1 flex justify-start group">
                      <div className="max-w-[75%] bg-amber-50 dark:bg-amber-950/20 border border-amber-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1.5 text-[10px]">
                          <MessageCircle className="h-3 w-3 shrink-0 text-amber-600" />
                          <span className="font-medium text-amber-700">{c.author_name}</span>
                          <span className="text-muted-foreground">{formatTimestamp(c.created_at)}</span>
                          <Button
                            variant="ghost" size="icon"
                            className="h-5 w-5 ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteComment(c.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-[11px] text-foreground/90 mt-0.5">{c.text}</p>
                      </div>
                    </div>
                  )
                }

                const message = item.message
                return (
                  <div key={message.id} ref={el => { if (el) messageRefs.current.set(message.id, el) }}>
                    <MessageBubble
                      message={message}
                      evalItems={evalItemMap}
                      agentFlags={agentFlags}
                      agentTitles={agentTitles}
                      onToggleFlag={(flagType, currentItems, currentFlags) => handleToggleFlag(message.id, flagType, currentItems, currentFlags, message)}
                      onResolveAll={(currentItems, currentFlags) => handleResolveAll(message.id, currentItems, currentFlags, message)}
                      onRate={async (rating, example) => {
                        setRatingOverrides(prev => ({ ...prev, [message.id]: rating }))
                        await submitRating(message.id, rating, example)
                      }}
                      comments={getCommentsForMessage(comments, message.id)}
                      onAddComment={text => handleAddComment(message.id, text)}
                      onDeleteComment={handleDeleteComment}
                    />
                  </div>
                )
              })
            )}
          </div>
        )}
      </ScrollArea>

      {/* ── No Reply Needed ── */}
      {showNoReplyBtn && (
        <div className="flex justify-center py-1 border-t">
          <Button
            variant="ghost" size="sm"
            className="h-6 text-[10px] text-muted-foreground hover:text-green-700 hover:bg-green-50"
            onClick={handleNoReplyNeeded}
          >
            <Check className="h-3 w-3 mr-1" />No Reply Needed
          </Button>
        </div>
      )}

      {/* ── Composer ── */}
      <div className="px-3 py-2 border-t">
        {showQuickReplies && (
          <QuickRepliesPanel
            onSelect={text => { setMessageContent(text); setShowQuickReplies(false) }}
            onClose={() => setShowQuickReplies(false)}
            contact={effectiveContact}
            messages={messages}
          />
        )}
        <div className="rounded-md border bg-background p-2 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" onClick={() => setShowQuickReplies(!showQuickReplies)} className="h-8 w-8">
                <Sparkles className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTemplateModalOpen(true)}
                className="h-8 w-8"
                title="Templates"
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleTogglePending}
                className={cn("h-8 w-8", isPending && "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100")}
                title={isPending
                  ? "No longer needs to be parked — remove from Pending"
                  : "No moderator action needed right now, but not resolved — park it in Pending"}
              >
                <Clock className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Button
              onClick={handleSend}
              disabled={sending || !messageContent.trim() || handledBy === "bot"}
              className="h-8 w-8"
              size="icon"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </Button>
          </div>

          <Textarea
            placeholder={handledBy === "human" ? "Type a message…" : "Switch to Human mode to reply"}
            value={messageContent}
            onChange={e => setMessageContent(e.target.value)}
            onKeyDown={e => {
              if (handledBy !== "human") return
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend() }
            }}
            className="w-full text-sm min-h-[88px] max-h-[240px] leading-5"
            rows={3}
            readOnly={handledBy === "bot"}
          />
        </div>
      </div>

    </div>
  )
}
